import { Collapse, LoadingIcon } from '@readcloud/component-library';
import { Book, Cloud, User } from '@readcloud/data';
import React, { useRef } from 'react';
import { BookList } from './components/BookList';
import TopNavBar from './components/TopNavBar';
import { useCloudsAndContentActions } from './context/ActionsContext';
import { CloudWithBooks } from './types';
import { SettingsModal } from './components/SettingsModal';
import { HomeSettings, cloudsAndContentSelectors } from '@readcloud/state';
import { ContentSettingsSheet } from './components/ContentSettingsSheet';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AuthContextUser } from '@readcloud/common';
import { useAppDispatch } from '@readcloud/state';
import cn from 'classnames';

export interface HomeProps {
  books: Book[];
  myUploads: Book[];
  clouds: CloudWithBooks[];
  user: AuthContextUser;
  userList: User[];
  settings: HomeSettings;
  moduleAllowed?: string[];
  refreshing: boolean;
  experimentalDesign?: boolean;
}

export function CloudsAndContent(props: HomeProps) {
  const [selectedCloudId, setSelectedCloudId] = React.useState<string>();
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [arrangingCloudId, setArrangingCloudId] = React.useState<string>();

  const toggleArrangingContent = (
    event: React.MouseEvent<HTMLDivElement>,
    cloudId?: string
  ) => {
    event.stopPropagation();
    setArrangingCloudId((current) =>
      current === cloudId ? undefined : cloudId
    );
  };
  const [bookSettingsOpen, setBookSettingsOpen] = React.useState<string | null>(
    null
  );
  const [selectedBook, setSelectedBook] = React.useState<Book | null>(null);

  const dispatch = useAppDispatch();
  const { scrollPosition } = useSelector(
    cloudsAndContentSelectors.getCloudsAndContentState
  );
  const actions = useCloudsAndContentActions();
  const searchParams = new URL(window.location.toString()).searchParams;
  const bookIsbn = searchParams.get('isbn');

  const history = useHistory();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasSetInitialScroll = useRef(false);

  const myUploadsCloud: CloudWithBooks = React.useMemo(() => {
    return {
      id: 'my-content',
      name: 'My Content',
      bookData: props.myUploads,
      permissions: {
        canAddContent: false,
        canRemoveContent: false,
        canEdit: false,
        canDelete: false,
      },
      admins: [props.user.id],
      addBookAdminOnly: true,
      institution: props.user.institution,
      description: '',
      type: 'custom',
      persistent: true,
      created: new Date().toISOString(),
      updated: new Date().toISOString(),
      deleted: false,
      books: [],
      members: [props.user.id],
      customBookSortOrder: false,
      institutions: [],
      resellers: [],
      order: 9999,
    };
  }, [props.myUploads, props.user]);

  const updatedClouds = React.useMemo(() => {
    return [myUploadsCloud, ...props.clouds].sort(
      (cloudA, cloudB) => cloudA.order - cloudB.order
    );
  }, [myUploadsCloud, props.clouds]);

  React.useEffect(() => {
    if (
      !props.refreshing &&
      scrollContainerRef.current &&
      scrollPosition &&
      !hasSetInitialScroll.current
    ) {
      scrollContainerRef.current.scrollTo(0, scrollPosition.containerPosition);

      const el = document.getElementById(
        scrollPosition.cloudId + '-inner-container' || ''
      );
      if (el) {
        el.scrollTo(scrollPosition.cloudPosition, 0);
      }
      hasSetInitialScroll.current = true;
    }
  }, [props.refreshing, scrollPosition]);

  const topNav = (
    <TopNavBar
      onToggleSettings={() => {
        setSettingsOpen((s) => !s);
      }}
      experimentalDesign={props.experimentalDesign}
      onCreateCloud={(data) => {
        return actions.createCloud(data).then((cloud) => {
          setSelectedCloudId(cloud.id);
          return cloud;
        });
      }}
      currentUserId={props.user.id}
      knownUsers={props.userList}
      onCloudSelected={setSelectedCloudId}
      clouds={updatedClouds}
      layout={props.settings.layout}
      onLayoutChange={(layout) => actions.updateSettings({ layout: layout })}
      onRefresh={actions.refresh}
      refreshing={props.refreshing}
      userFullName={`${props.user.firstName} ${props.user.lastName}`}
      userEmail={props.user.email}
    />
  );

  React.useEffect(() => {
    if (bookIsbn) {
      const foundInAllClouds: Cloud[] = [];
      const book = props.books?.find((book) => book.isbn === bookIsbn);

      if (
        book &&
        //This is the only way to tell if it's meant to be opened with reader.
        book.pdfURI !== 'nonBook' &&
        !book.intLoginData &&
        book.type !== 'Media'
      ) {
        props.clouds.forEach((cloud) => {
          cloud.books.forEach((bookId) => {
            if (book.id === bookId) {
              foundInAllClouds.push(cloud);
            }
          });
        });

        const foundInNonSystemClouds = foundInAllClouds.filter(
          (cloud) => cloud.type !== 'System'
        );

        if (
          foundInNonSystemClouds.length === 0 &&
          foundInAllClouds.length > 0
        ) {
          history.replace(`/read/${foundInAllClouds[0].id}/${book.id}`);
        } else if (foundInNonSystemClouds.length > 0) {
          history.replace(`/read/${foundInNonSystemClouds[0].id}/${book.id}`);
        }
      }
    }
  }, [bookIsbn, props.clouds.length, props.books.length]);

  return (
    /*
      Using this layout, where the nav is a statically/relatively positioned element, and the scrollable area (clouds) is a child div,
      allows us to more reliably and predictably implement "scroll into view" functionality. Otherwise, offset hacks must be used in order to
      scroll an element "accurately" into view; i.e. you don't need to know the height of the nav in order to add scroll-padding, or
      similar in a JS call to offset the scroll.
    */
    <>
      <div className="rc-h-full">
        <div
          className="rc-flex rc-flex-col rc-relative rc-h-full"
          style={{
            filter: props.refreshing ? 'blur(3px)' : 'unset',
            overflow: props.refreshing ? 'hidden' : 'auto',
          }}
        >
          {!props.experimentalDesign && topNav}
          <Collapse timeout={150} isOpen={props.refreshing}>
            <div
              className="rc-flex rc-justify-center rc-items-center"
              style={{ height: '7rem', width: '100%' }}
            >
              <LoadingIcon loading />
            </div>
          </Collapse>

          <div
            className={cn(
              'rc-flex-1 rc-pt-2 rc-pb-4',
              arrangingCloudId ? 'rc-overflow-hidden' : 'rc-overflow-auto'
            )}
            style={{ paddingBottom: '1rem' }}
            id={'scroll-container'}
            ref={scrollContainerRef}
          >
            <BookList
              knownUsers={props.userList}
              selectedCloudId={selectedCloudId}
              currentUserId={props.user.id}
              books={props.books}
              clouds={updatedClouds}
              settings={props.settings}
              user={props.user}
              scrollContainerRef={scrollContainerRef}
              arrangingCloudId={arrangingCloudId}
              toggleArrangingContent={toggleArrangingContent}
              setBookSettingsOpen={(bookId) => {
                setBookSettingsOpen(bookId);
                if (bookId) {
                  const book = props.books.find((b) => b.id === bookId) || null;
                  setSelectedBook(book);
                } else {
                  setSelectedBook(null);
                }
              }}
            />
          </div>
        </div>
      </div>
      <SettingsModal
        open={settingsOpen}
        onToggle={() => {
          setSettingsOpen((s) => !s);
        }}
      />

      <ContentSettingsSheet
        book={selectedBook}
        open={selectedBook !== null}
        onOpenChange={(open) => {
          if (!open) {
            setBookSettingsOpen(null);
            setSelectedBook(null);
          }
        }}
      />
    </>
  );
}
